import { useTranslation } from 'react-i18next';
import { formatUZS } from '@/lib/money';
import { formatDateTime, type LocaleKey } from '@/lib/time';
import { useUIStore } from '@/stores/uiStore';
import { groupOrderItems, itemQty } from '@/lib/orderItems';
import type { OrderDTO } from '@/types/api';

/**
 * Hidden by default. Rendered into the DOM and revealed only by the `@media
 * print` rules in index.css; the rest of the page is hidden during print.
 *
 * Mount this once near the order being printed and call `window.print()`.
 */
export function Receipt({ order }: { order: OrderDTO }) {
  const { t } = useTranslation();
  const lang = useUIStore((s) => s.language) as LocaleKey;
  const items = order.items ?? [];
  return (
    <div id="receipt" className="receipt">
      <div className="receipt-header">
        <strong>HOOPLA POS</strong>
        <div className="receipt-order">#{order.id}</div>
        <div className="receipt-meta">{formatDateTime(order.createdAt, lang)}</div>
        {order.tableName && (
          <div className="receipt-meta">{t('orders.detail.table')}: {order.tableName}</div>
        )}
        {order.customerPhone && (
          <div className="receipt-meta">{t('receipt.customer')}: {order.customerPhone}</div>
        )}
      </div>

      <div className="receipt-rule" />

      <table className="receipt-items">
        <tbody>
          {items.length === 0 ? (
            <tr>
              <td className="receipt-name" colSpan={2}>—</td>
            </tr>
          ) : (
            groupOrderItems(items).flatMap(({ drink, modifiers }) => {
              const qty = itemQty(drink);
              const rows = [
                <tr key={drink.id}>
                  <td className="receipt-name">
                    {drink.name}
                    {qty > 1 ? ` ×${qty}` : ''}
                  </td>
                  <td className="receipt-price">{formatUZS(drink.price * qty, lang)}</td>
                </tr>,
              ];
              modifiers.forEach((mod) => {
                const modQty = itemQty(mod);
                rows.push(
                  <tr key={mod.id}>
                    <td className="receipt-name receipt-modifier-name">
                      {mod.name}
                      {modQty > 1 ? ` ×${modQty}` : ''}
                    </td>
                    <td className="receipt-price">
                      {mod.price > 0 ? `+${formatUZS(mod.price * modQty, lang)}` : ''}
                    </td>
                  </tr>,
                );
              });
              return rows;
            })
          )}
        </tbody>
      </table>

      <div className="receipt-rule" />

      {order.comment && (
        <div className="receipt-comment">
          <strong>{t('receipt.comment')}:</strong> {order.comment}
        </div>
      )}

      <table className="receipt-totals">
        <tbody>
          <tr className="receipt-total">
            <td>{t('receipt.total')}</td>
            <td className="receipt-price">{formatUZS(order.productPrice, lang)}</td>
          </tr>
          <tr>
            <td colSpan={2} style={{ textAlign: 'center', paddingTop: '4px' }}>
              {t('receipt.orderedViaHoopla')}
            </td>
          </tr>
        </tbody>
      </table>

      <div className="receipt-footer">{t('receipt.thanks')}</div>
    </div>
  );
}
